import { useEffect, useMemo, useState } from 'react';
import { ApiError, exchangeSession, makeApi, type Api } from './api';
import { LANGUAGES, dirOf, makeT, pickLanguage, type T } from './i18n';
import { PERM, PermsContext, canWith } from './access';
import { NoAccess } from './ui';
import { AgentPage } from './pages/Agent';
import { AgentsPage } from './pages/Agents';
import { CallDetailPage } from './pages/CallDetail';
import { CallsPage } from './pages/Calls';
import { CampaignPage, CampaignsPage } from './pages/Campaigns';
import { ContactsPage } from './pages/Contacts';
import { LeadsPage } from './pages/Leads';
import { OverviewPage } from './pages/Overview';
import { StudioPage } from './pages/Studio';
import { TelephonyPage } from './pages/Telephony';
import { AuditPage } from './pages/Audit';
import { PrivacyPage } from './pages/Privacy';
import { TestRoomPage } from './pages/TestRoom';
import { ReadyAgentsPage } from './pages/ReadyAgents';
import { StatsPage } from './pages/Stats';
import { PlatformCallsPage } from './pages/PlatformCalls';
import { PlatformNeedsPage } from './pages/PlatformNeeds';

const SSO_URL = import.meta.env.VITE_PANARYUS_SSO_URL ?? '/sso/session';
const LOGIN_URL = import.meta.env.VITE_PANARYUS_LOGIN_URL ?? 'https://panaryus.com/login';
const API_BASE = `${import.meta.env.BASE_URL}api`;

type Route = 'overview' | 'agents' | 'studio' | 'agent' | 'calls' | 'call' | 'contacts' | 'leads' | 'campaigns' | 'campaign' | 'telephony' | 'room' | 'templates' | 'stats' | 'needs' | 'aiCalls' | 'audit' | 'privacy';
const ROUTES: readonly Route[] = ['overview', 'agents', 'studio', 'agent', 'calls', 'call', 'contacts', 'leads', 'campaigns', 'campaign', 'telephony', 'room', 'templates', 'stats', 'needs', 'aiCalls', 'audit', 'privacy'];

/** Grouped like the operator's day (layout from the legacy panel): daily work, building, setup. */
/** ADR-071: pages that need more than seeing (same as their menu entries; also checked when opened by address). */
const ROUTE_NEED: Partial<Record<Route, string>> = {
  templates: PERM.agents,
  studio: PERM.agents,
  room: PERM.call,
  telephony: PERM.lines,
  privacy: PERM.compliance,
  audit: PERM.compliance,
};

/** ADR-071: `need` = the Call Center permission the page needs (team permissions from Panaryus). */
type NavItem = { id: string; route?: Route; params?: Record<string, string>; icon: string; need?: string };
/** Shown only to platform operators (Panaryus super-admins): platform-wide screens. */
const PLATFORM_NAV: { group: string; items: NavItem[] } = { group: 'platform', items: [{ id: 'templateNeeds', route: 'needs', icon: '◈' }, { id: 'aiCalls', route: 'aiCalls', icon: '◷' }] };
const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'daily',
    items: [
      { id: 'overview', route: 'overview', icon: '⌂' },
      { id: 'conversations', route: 'calls', icon: '◉' },
      { id: 'callbacks', route: 'calls', params: { tab: 'callbacks' }, icon: '↺' },
      { id: 'campaigns', route: 'campaigns', icon: '▤' },
      { id: 'contacts', route: 'contacts', icon: '☰' },
      { id: 'leads', route: 'leads', icon: '★' },
      { id: 'stats', route: 'stats', icon: '▥' },
    ],
  },
  {
    group: 'build',
    items: [
      { id: 'agents', route: 'agents', icon: '✦' },
      { id: 'readyAgents', route: 'templates', icon: '▣', need: 'centeros.agents' },
      { id: 'studio', route: 'studio', icon: '✎', need: 'centeros.agents' },
      { id: 'testRoom', route: 'room', icon: '◎', need: 'centeros.call' },
    ],
  },
  {
    group: 'setup',
    items: [
      { id: 'doNotCall', route: 'calls', params: { tab: 'dnc' }, icon: '⊘' },
      { id: 'telephony', route: 'telephony', icon: '☏', need: 'centeros.lines' },
      { id: 'privacy', route: 'privacy', icon: '⛉', need: 'centeros.compliance' },
      { id: 'audit', route: 'audit', icon: '⧉', need: 'centeros.compliance' },
      { id: 'usage', icon: '▦' },
    ],
  },
];

const routeFromUrl = (): Route => {
  const p = new URLSearchParams(location.search).get('p') as Route | null;
  return p && ROUTES.includes(p) ? p : 'overview';
};
const param = (k: string): string => new URLSearchParams(location.search).get(k) ?? '';

type Theme = 'auto' | 'dark' | 'light';
const readTheme = (): Theme => {
  try {
    const v = localStorage.getItem('cav2.theme');
    return v === 'dark' || v === 'light' ? v : 'auto';
  } catch {
    return 'auto';
  }
};

export const App = () => {
  const [lang, setLang] = useState<string>(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem('cav2.lang');
    } catch {
      // storage blocked: browser language
    }
    return pickLanguage(saved, navigator.language || 'en');
  });
  const t: T = useMemo(() => makeT(lang), [lang]);
  // The page speaks the chosen language, right to left where it is written that way.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dirOf(lang);
    try {
      localStorage.setItem('cav2.lang', lang);
    } catch {
      // storage blocked: the choice lasts for this visit
    }
  }, [lang]);
  const [api, setApi] = useState<Api>();
  const [platformAdmin, setPlatformAdmin] = useState(false);
  // ADR-071: what this person may do (null until known: nothing is hidden before the answer).
  const [perms, setPerms] = useState<Set<string> | null>(null);
  // Pages open once /me has answered (or failed): a page never fires a request its person would be refused
  // (each refusal is written to the activity log) and buttons do not appear and then vanish.
  const [meDone, setMeDone] = useState(false);
  useEffect(() => void api?.me().then((m) => {
    setPlatformAdmin(m.platformAdmin);
    if (m.permissions) setPerms(new Set(m.permissions));
  }).catch(() => undefined).finally(() => setMeDone(true)), [api]);
  const allowed = (n: NavItem) => canWith(perms, n.need);
  const [error, setError] = useState<string>();
  const [route, setRoute] = useState<Route>(routeFromUrl);
  /** ADR-071: a page opened by its address needs the same permission as its menu entry. */
  const routeNeed = ROUTE_NEED[route];
  const pageAllowed = canWith(perms, routeNeed);
  const [search, setSearch] = useState(location.search);
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    exchangeSession(SSO_URL)
      .then((token) => setApi(makeApi(API_BASE, token)))
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.code === 'UNAUTHENTICATED') {
          location.href = `${LOGIN_URL}?redirect=${encodeURIComponent(location.href)}`;
          return;
        }
        setError(e instanceof ApiError ? e.code : 'generic');
      });
  }, []);

  useEffect(() => {
    const onPop = () => {
      setRoute(routeFromUrl());
      setSearch(location.search);
    };
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (theme === 'auto') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('cav2.theme', theme);
    } catch {
      /* a private window: the choice lasts this visit */
    }
  }, [theme]);

  const go = (r: Route, params: Record<string, string> = {}) => {
    const q = new URLSearchParams({ p: r, ...params });
    history.pushState(null, '', `?${q}`);
    setRoute(r);
    setSearch(`?${q}`);
    scrollTo(0, 0);
  };
  const active = (n: NavItem): boolean => {
    if (!n.route) return false;
    const tab = new URLSearchParams(search).get('tab') ?? '';
    if (n.route === 'calls') return (route === 'calls' || route === 'call') && (n.params?.tab ?? '') === (route === 'calls' ? tab : '');
    return n.route === route || (route === 'agent' && n.route === 'agents') || (route === 'campaign' && n.route === 'campaigns');
  };
  const title = route === 'campaign' ? 'campaigns' : route === 'agent' ? 'agents' : route === 'call' ? 'conversations' : route === 'calls' ? (param('tab') === 'callbacks' ? 'callbacks' : param('tab') === 'dnc' ? 'doNotCall' : 'conversations') : route;
  const isDark = theme === 'dark' || (theme === 'auto' && matchMedia('(prefers-color-scheme: dark)').matches);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">◆</span>
          <span>
            {t('app.title')}
            <small className="brand-sub">{t('app.subtitle')}</small>
          </span>
        </div>
        {(platformAdmin ? [...NAV, PLATFORM_NAV] : NAV).map((g) => (
          <nav key={g.group} aria-label={t(`nav.group.${g.group}`)}>
            <div className="nav-group">{t(`nav.group.${g.group}`)}</div>
            {g.items.filter(allowed).map((n) => (
              <button
                key={n.id}
                type="button"
                className={`nav-item ${active(n) ? 'active' : ''}`}
                disabled={!n.route}
                onClick={() => n.route && go(n.route, n.params ?? {})}
              >
                <span className="nav-label"><span className="nav-icon" aria-hidden>{n.icon}</span>{t(`nav.${n.id}`)}</span>
                {!n.route && <span className="soon">{t('nav.soon')}</span>}
              </button>
            ))}
          </nav>
        ))}
      </aside>
      <div className="main">
        <header className="topbar">
          <div>
            <div className="topbar-kicker">{t('app.workspace')}</div>
            <div className="topbar-title">{t(`nav.${title}`)}</div>
          </div>
          <div className="topbar-actions">
          <select className="lang-select" value={lang} aria-label={t('lang.label')} onChange={(e) => setLang(e.target.value)}>
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
          <div className="theme-toggle" role="group" aria-label={t('theme.label')}>
            <button type="button" className={isDark ? 'on' : ''} onClick={() => setTheme('dark')}>{t('theme.dark')}</button>
            <button type="button" className={!isDark ? 'on' : ''} onClick={() => setTheme('light')}>{t('theme.light')}</button>
          </div>
          </div>
        </header>
        <main className="content">
          <PermsContext value={perms}>
          {api && meDone && !pageAllowed && routeNeed && <NoAccess t={t} need={routeNeed} />}
          {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? `${t('boot.failed')} (${error})` : t(`error.${error}`)}</div>}
          {(!api || !meDone) && !error && <div className="muted center">{t('boot.loading')}</div>}
          {api && meDone && route === 'overview' && <OverviewPage api={api} t={t} go={go} />}
          {api && meDone && route === 'agents' && <AgentsPage api={api} t={t} onNew={() => go('studio')} onOpen={(id) => go('agent', { id })} />}
          {api && meDone && route === 'agent' && <AgentPage key={param('id')} api={api} t={t} agentId={param('id')} initialTab={param('tab') || undefined} focus={param('q') || undefined} onBack={() => go('agents')} onOpenCall={(id) => go('call', { id })} />}
          {api && meDone && route === 'calls' && (
            <CallsPage
              key={search}
              api={api}
              t={t}
              initialTab={((x) => (x === 'callbacks' || x === 'dnc' || x === 'web' ? x : 'all'))(param('tab'))}
              initialCall={param('call') || undefined}
              initialSameAs={param('sameAs') || undefined}
              onOpen={(id) => go('call', { id })}
              onSameNumber={(id) => go('calls', { sameAs: id })}
              onClearSameAs={() => go('calls')}
              onTab={(tab) => {
                const q = `?${new URLSearchParams({ p: 'calls', tab })}`;
                history.replaceState(null, '', q);
                setSearch(q);
              }}
            />
          )}
          {api && meDone && route === 'call' && <CallDetailPage key={param('id')} api={api} t={t} callId={param('id')} onBack={() => go('calls')} onOpen={(id) => go('call', { id })} onSameNumber={(id) => go('calls', { sameAs: id })} />}
          {api && meDone && route === 'contacts' && <ContactsPage api={api} t={t} />}
          {api && meDone && pageAllowed && route === 'telephony' && <TelephonyPage api={api} t={t} />}
          {api && meDone && pageAllowed && route === 'audit' && <AuditPage api={api} t={t} />}
          {api && meDone && pageAllowed && route === 'privacy' && <PrivacyPage api={api} t={t} />}
          {api && meDone && pageAllowed && route === 'room' && <TestRoomPage api={api} t={t} onOpen={(id) => go('call', { id })} />}
          {api && meDone && route === 'leads' && <LeadsPage api={api} t={t} onOpenCall={(id) => go('call', { id })} />}
          {api && meDone && route === 'campaigns' && <CampaignsPage key={search} api={api} t={t} go={go} />}
          {api && meDone && route === 'campaign' && <CampaignPage key={param('id')} api={api} t={t} id={param('id')} go={go} />}
          {api && meDone && pageAllowed && route === 'studio' && <StudioPage api={api} t={t} onDone={(id) => (id ? go('agent', { id, tab: 'voice' }) : go('agents'))} onReady={() => go('templates')} />}
          {api && meDone && route === 'stats' && <StatsPage api={api} t={t} go={go} />}
          {api && meDone && route === 'needs' && platformAdmin && <PlatformNeedsPage api={api} t={t} />}
          {api && meDone && route === 'aiCalls' && platformAdmin && <PlatformCallsPage key={param('id')} api={api} t={t} {...(param('id') ? { callId: param('id') } : {})} onOpen={(id) => go('aiCalls', { id })} onBack={() => go('aiCalls')} />}
          {api && meDone && pageAllowed && route === 'templates' && <ReadyAgentsPage api={api} t={t} onCreated={(id) => go('agent', { id })} onStudio={() => go('studio')} onStudioSession={(session) => go('studio', { session })} />}
          </PermsContext>
        </main>
      </div>
    </div>
  );
};
